import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useLobbyStore } from '../../store/lobby-store';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { useVisibility } from '../../hooks/useVisibility';
import { useSchermoSveglio } from '../../hooks/useSchermoSveglio';
import { ATTESA_PRIMA_DI_USCIRE_DALLA_CODA_MS } from '../../hooks/useArenaQueueGuard';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { parseTimeControl } from '../../utils/cadence-icons';
import { trackTableCancelled } from '../../utils/analytics';
import { log } from '../../utils/logger';
import './QueuedTableBanner.css';

interface Props {
  /**
   * Riapre il lobby. Opzionale: senza, il banner resta quello di prima (mostra
   * l'attesa e la annulla), così può essere montato da solo nei test.
   */
  onOpenLobby?: () => void;
}

/**
 * "Sei in coda" — the compact reminder that a table of yours is still open.
 *
 * A table deliberately outlives the overlay that opened it: that is what lets
 * someone actually find it while you read the FAQ. The flip side is that a game
 * can start while nothing lobby-shaped is on screen, so the wait has to stay
 * visible, and cancellable, from everywhere. Mounted alongside the challenge
 * overlays, which every screen already renders.
 *
 * E siccome l'attesa è visibile da ovunque, anche la porta per tornarci lo è:
 * la riga è un bottone che riapre "Gioca". Prima l'unica azione offerta a chi
 * era in coda era la ✕, cioè uscirne.
 */
export default function QueuedTableBanner({ onOpenLobby }: Props) {
  const { t } = useTranslation();
  const myTableId = useLobbyStore((s) => s.myTableId);
  const myTableSince = useLobbyStore((s) => s.myTableSince);
  /*
   * Due stringhe, non la lista.
   *
   * Questo banner e' montato SU OGNI SCHERMATA (App.tsx, accanto alle
   * sovrimpressioni delle sfide) e di `s.tables` usa due campi del solo tavolo
   * mio. Abbonarsi alla lista voleva dire risvegliarsi a ogni
   * `lobby:table-added` e `lobby:table-removed` — che con la flotta dei bot di
   * presenza arrivano da soli, ogni mezzo minuto, anche a chi sta leggendo le
   * regole — per poi tornare `null` due righe dopo, perche' `myTableId` e'
   * nullo.
   *
   * Perche' i CAMPI e non il tavolo: `addTable` e `removeTable` allocano un
   * array nuovo (store/lobby-store.ts) ma non toccano gli oggetti dentro,
   * quindi contro quei due basterebbe un selettore che torni il tavolo.
   * `setSnapshot` invece rimpiazza anche gli oggetti, con dati identici, a ogni
   * riconnessione: contro quello regge solo il confronto su due stringhe.
   *
   * Nessuna guardia su `myTableId` dentro i selettori: quando e' nullo, `find`
   * torna `undefined` da se'.
   */
  const variante = useLobbyStore((s) => s.tables.find((tbl) => tbl.id === s.myTableId)?.variant ?? null);
  const cadenza = useLobbyStore((s) => s.tables.find((tbl) => tbl.id === s.myTableId)?.timeControl ?? null);
  const cancelMyTable = useMultiplayerStore((s) => s.cancelMyTable);
  const [elapsed, setElapsed] = useState(0);

  /*
   * Lo schermo resta acceso mentre si aspetta a un tavolo.
   *
   * Qui non e' una comodita': la guardia qui sotto esce dalla coda dopo cinque
   * secondi in secondo piano, e lo schermo che si spegne porta la' in un attimo.
   * Chi guarda il proprio tavolo aspettando qualcuno non deve perderlo per non
   * aver toccato niente.
   *
   * Con l'argomento e non col montaggio: questo banner e' montato su OGNI
   * schermata e si spegne col ritorno anticipato qui sotto: legato al
   * montaggio, terrebbe acceso lo schermo di tutta l'app, per sempre.
   */
  useSchermoSveglio(Boolean(myTableId));

  useEffect(() => {
    if (!myTableId || !myTableSince) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - myTableSince) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [myTableId, myTableSince]);

  /*
   * Mandata l'app in secondo piano mentre si è in coda, si esce dalla coda.
   *
   * Stessa regola dell'arena (`useArenaQueueGuard`) e stessa che aveva la
   * vecchia schermata di ricerca: chi mette l'app in secondo piano restando in
   * coda verrebbe accoppiato lo stesso, l'orologio partirebbe e la partita si
   * perderebbe a tempo senza che l'utente l'abbia mai vista.
   *
   * La guardia vive QUI e non nell'overlay proprio perché il tavolo sopravvive
   * alla chiusura dell'overlay: legarla a un componente che può non essere
   * montato la renderebbe una guardia a intermittenza.
   *
   * ⚠️ E con un'attesa: `visibilitychange` scatta anche per un alt-tab su
   * desktop, e perdere il posto in coda per due secondi passati in un'altra
   * scheda sarebbe una punizione per un gesto normale. Stessa costante
   * dell'arena, perché è la stessa domanda.
   */
  const timerUscitaCoda = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fermaUscitaCoda = () => {
    if (timerUscitaCoda.current === null) return;
    clearTimeout(timerUscitaCoda.current);
    timerUscitaCoda.current = null;
  };

  /*
   * ⚠️ Mai durante una partita.
   *
   * Il banner e' negli overlay globali, quindi si disegna anche sopra la
   * scacchiera: finche' `myTableId` restava appeso a partita iniziata (vedi
   * `onGameStart` nel multiplayer-store), mandare l'app in secondo piano
   * DURANTE la partita faceva partire questo timer e chiamare `cancelMyTable()`
   * su una coda che non esisteva piu'. Ora quel caso non dovrebbe piu' nascere,
   * ma la guardia non deve essere raggiungibile per costruzione, non per
   * fortuna.
   */
  const inPartita = () => useMultiplayerStore.getState().status === 'playing';

  useVisibility({
    onHidden: () => {
      if (!useLobbyStore.getState().myTableId) return;
      if (inPartita()) return;
      fermaUscitaCoda();
      timerUscitaCoda.current = setTimeout(() => {
        timerUscitaCoda.current = null;
        /* Rileggere adesso: nei secondi d'attesa si può essere tornati, o
           qualcuno si può già essere seduto. */
        if (document.visibilityState !== 'hidden') return;
        if (!useLobbyStore.getState().myTableId) return;
        if (inPartita()) return;
        log.info('QueuedTableBanner', 'app in secondo piano: esco dalla coda');
        trackTableCancelled();
        useMultiplayerStore.getState().cancelMyTable();
      }, ATTESA_PRIMA_DI_USCIRE_DALLA_CODA_MS);
    },
    onVisible: fermaUscitaCoda,
  });

  useEffect(() => fermaUscitaCoda, []);

  if (!myTableId) return null;

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const clock = cadenza ? parseTimeControl(cadenza)?.clock : null;

  return (
    <div className="queued-table-banner" role="status">
      {/* La riga apre, la ✕ annulla: due bersagli separati, perché un bottone
          dentro un bottone non è HTML valido e perché annullare per sbaglio il
          proprio tavolo mentre si voleva solo riaprirlo è il danno peggiore. */}
      <button
        type="button"
        className="queued-table-open"
        aria-label={t('lobby.title')}
        onClick={() => onOpenLobby?.()}
      >
        <span className="queued-table-spinner" aria-hidden="true" />
        <span className="queued-table-text">
          {t('lobby.inQueue')}
          {variante && (
            <span className="queued-table-detail">
              {variantPill(variante).flag} {variantShortLabel(t, variante)}
              {clock ? ` · ${clock}` : ''}
            </span>
          )}
        </span>
        <span className="queued-table-timer">{mins}:{String(secs).padStart(2, '0')}</span>
      </button>
      <button
        type="button"
        className="queued-table-cancel"
        aria-label={t('lobby.cancelTable')}
        onClick={() => { trackTableCancelled(); cancelMyTable(); }}
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
